<script setup lang="ts">
import { generateSeoLinks } from '~/lib/seo'
import CheckoutMainHeader from '~/components/checkout/MainHeader.vue'
import CheckoutShippingBar from '~/components/checkout/ShippingBar.vue'
import LoadingSpinner from '~/components/loading/LoadingSpinner.vue'
import { useCartStore } from '~/stores/cart'
import { useCheckoutStore } from '~/stores/checkout'
import { useInitStore } from '~/stores/init'
import { useRouteStore } from '~/stores/route'

// TODO: Add transition of sidebar
definePageMeta({
  isCheckoutPage: true,
})

const route = useRoute()

const cartStore = useCartStore()
const checkoutStore = useCheckoutStore()
const initStore = useInitStore()

const { loading: checkoutLoading } = storeToRefs(checkoutStore)
const { loading: cartLoading } = storeToRefs(cartStore)
const { loading: initLoading } = storeToRefs(initStore)
const { checkoutReturnRoute } = storeToRefs(useRouteStore())

const checkout = ref(null)

const isCartView = computed(() => route.meta.view === 'cart')

const isLoading = computed(() => cartLoading.value || checkoutLoading.value || initLoading.value)

useHead({
  meta: [
    { key: 'robots', name: 'robots', content: 'noindex' },
    { key: 'description', name: 'description', content: ' ' },
  ],
  bodyAttrs: {
    class: 'immersive-page locked',
  },
  link: generateSeoLinks(route),
})
</script>

<template>
  <div class="child-page">
    <a
      class="background"
      :aria-label="$t('close')"
    />

    <div class="body">
      <section
        ref="checkout"
        :aria-label="$t('checkOut')"
        class="checkout"
        :class="[route.meta.view, { loading: isLoading }]"
      >
        <!-- <LoadingSpinner v-if="isLoading" :size="5" /> -->
        <ClientOnly>
          <CheckoutMainHeader :return-route="checkoutReturnRoute" />
          <div
            v-if="cartStore.state.items.length && !isCartView"
            class="shipping-bar__container"
          >
            <CheckoutShippingBar />
          </div>
          <NuxtPage
            :key="route.fullPath"
            keepalive
          />
        </ClientOnly>
      </section>
    </div>
  </div>
</template>

<style scoped>
.shipping-bar__container {
  padding: 0 1.6rem;

  @media screen and (min-width: 480px) {
    padding: 0 calc(10% + 1.5rem);
  }
  @media screen and (min-width: 768px) {
    padding: 0 calc(20% + 1.5rem);
  }
  @media screen and (min-width: 1440px) {
    padding: 0 calc(25% + 1.5rem);
  }
}
.child-page {
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10;
  height: 100%;
  height: 100vh;
  display: grid;
  position: fixed;
}
.background {
  z-index: 2;
  display: none;
  visibility: hidden;
}
.body {
  z-index: 5;
  display: grid;
}

.checkout {
  display: flex;
  position: relative;
  flex-direction: column;
  padding-top: var(--header-and-top-banner-height);
  width: 100vw;
  height: min(100vh, var(--window-height));
  background: var(--white);
  overflow-y: scroll;

  .loader {
    top: 50%;
    left: 50%;
    z-index: 1000;
    position: absolute;
    transform: translate(-50%, -50%);
  }

  &.loading {
    .main-content {
      filter: blur(0.5rem);
    }
    &::before {
      content: ' ';
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 100;
      position: absolute;
      background: rgba(0, 0, 0, 0.3);
    }
  }

  &:not(.payment):not(.address) #checkoutBanner {
    display: none;
  }
}

.main-content {
  height: 100%;
  flex: 1 1 auto;
  overflow-y: auto;
  padding-bottom: var(--checkout-summary-height);
}

@media screen and (min-width: 480px) {
  .checkout {
    padding-top: var(--header-and-top-banner-height);
    /* TODO: Add code above and commented lines below to do the checkout full screen. it should be reverted and fixed after 1 August. */
    /* max-width: 48rem; */
    /* width: calc(100vw - 5rem); */
    /* height: calc(min(100vh, var(--window-height)) - var(--header-main-height)); */
    /* border-left: 0.2rem solid var(--black); */
    /* box-shadow: -0.2rem 0 2rem 0 rgba(0, 0, 0, 0.1); */
  }

  .main-content {
    padding-bottom: 0;
  }
  .checkout .banner {
    width: 100%;
  }
  .child-page {
    /* TODO: commented lines below to do the checkout full screen. it should be reverted and fixed after 1 August. */
    /* top: var(--header-main-height); */
    height: calc(100vh - var(--header-main-height));
  }
  .background {
    width: 200%;
    height: 100%;
    display: block;
    cursor: pointer;
    position: absolute;
    margin-left: -100%;
    visibility: visible;
    will-change: opacity;
    animation: background 0.3s 1;
    background: rgba(0, 0, 0, 0.2);
  }
  .body {
    height: 100%;
    justify-self: end;
    position: relative;
  }
  /* Modal animations */
  .modal-enter-active,
  .modal-leave-active {
    transition: transform 0.3s cubic-bezier(0.5, 0, 0, 1);
  }
  .modal-enter,
  .modal-leave-to {
    transform: translateX(100%);
  }
  .modal-leave-active #modal-background,
  .modal-active #modal-background {
    display: none;
  }
}

@media screen and (min-width: 768px) {
  .checkout {
    padding-top: var(--header-and-top-banner-height);
  }
}

@media screen and (min-width: 1440px) {
  .checkout {
    padding-top: var(--header-and-top-banner-height);
  }
}
</style>
